import React, { useEffect, useState } from "react";
import { useAppUserDetailsSelector, useAppUserPermissionsSelector } from "src/_common/hooks/selectors/userSelector";
import { hasPermission } from "src/_common/utils/permissionsUtils";
import { useUserApi } from "src/_common/hooks/actions/user/appUserApiHook";
import { useTranslation } from "src/_common/hooks/useTranslation";
import moment from "moment";

interface ActivityData {
  id: string;
  created_at: string;
  user_details?: any;
  action: string;
  details: string;
  ip_address: string;
}

interface AllUsersTabProps {}

const AllUsersTab: React.FC<AllUsersTabProps> = () => {
  const userDetails = useAppUserDetailsSelector();
  const userPermissions = useAppUserPermissionsSelector();
  const { callGetAllUsersActivity } = useUserApi();
  const { t } = useTranslation("settings");

  const [data, setData] = useState<ActivityData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 10;

  // Helper function to check permissions for member users
  const canAccessMenu = (menuName: string): boolean => {
    // For members, strictly check permissions - only show if they have access
    if (userDetails?.user_type === "member") {
      return hasPermission(userPermissions, menuName);
    }

    // Non-members (employers/admins) have access to all menus
    return true;
  };

  // Check permissions for different actions
  const canReadHistoryAllUsers = canAccessMenu("read_history_all_users");

  // Load activity data
  const loadActivityData = (pages: number = 1) => {
    setIsLoading(true);
    const page = pages - 1; // Convert to 0-indexed for API
    callGetAllUsersActivity(
      { per_page: itemsPerPage, page },
      (message: string, response: any) => {
        console.log("All users activity data:", response);
        // New structure: response.user_activities contains the activities array
        setData(response.user_activities || []);
        // New structure: response.pagination contains the pagination info
        setTotalPages(Math.ceil((response.pagination?.total || 0) / itemsPerPage));
        setTotalCount(response.pagination?.total || 0);
        setCurrentPage(pages);
        setIsLoading(false);
      },
      (error: any) => {
        console.error("Error loading all users activity:", error);
        setIsLoading(false);
      }
    );
  };

  useEffect(() => {
    loadActivityData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      loadActivityData(page);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      loadActivityData(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      loadActivityData(currentPage + 1);
    }
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(
          <button
            key={i}
            className={`page-num ${currentPage === i ? "active" : ""}`}
            onClick={() => handlePageChange(i)}
          >
            {i}
          </button>
        );
      }
    } else {
      // Show first page
      pages.push(
        <button key={1} className={`page-num ${currentPage === 1 ? "active" : ""}`} onClick={() => handlePageChange(1)}>
          1
        </button>
      );

      // Show dots if current page is far from start
      if (currentPage > 3) {
        pages.push(<span key="dots-start">...</span>);
      }

      // Show pages around current page
      const startPage = Math.max(2, currentPage - 1);
      const endPage = Math.min(totalPages - 1, currentPage + 1);

      for (let i = startPage; i <= endPage; i++) {
        pages.push(
          <button
            key={i}
            className={`page-num ${currentPage === i ? "active" : ""}`}
            onClick={() => handlePageChange(i)}
          >
            {i}
          </button>
        );
      }

      // Show dots if current page is far from end
      if (currentPage < totalPages - 2) {
        pages.push(<span key="dots-end">...</span>);
      }

      // Show last page
      pages.push(
        <button
          key={totalPages}
          className={`page-num ${currentPage === totalPages ? "active" : ""}`}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </button>
      );
    }

    return pages;
  };

  // If member doesn't have read permission, don't render the component
  if (userDetails?.user_type === "member" && !canReadHistoryAllUsers) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>{t("history.loadingAllUsers")}</p>
      </div>
    );
  }

  return (
    <div className="history-content">
      <div className="history-table-container">
        <table className="history-table">
          <thead>
            <tr>
              <th>{t("history.table.dateTime")}</th>
              <th>{t("history.table.user")}</th>
              <th>{t("history.table.action")}</th>
              <th>{t("history.table.details")}</th>
              <th>{t("history.table.ipAddress")}</th>
            </tr>
          </thead>
          <tbody>
            {data.map((activity) => (
              <tr key={activity.id}>
                <td className="date-time">{moment(activity.created_at).format("MMM D, YYYY – h:mm A")}</td>
                <td className="user">
                  {/* {activity.user_details.user_type == "employer"
                    ? activity.user_details.company_name
                    : activity.user_details.name} */}
                  {activity.user_details.name}
                </td>
                <td className="action">{activity.action}</td>
                <td className="details">{activity.details}</td>
                <td className="ip-address">{activity.ip_address}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button className="page-btn" onClick={handlePrevious} disabled={currentPage === 1}>
            {t("history.pagination.prev")}
          </button>
          <div className="page-numbers">{renderPageNumbers()}</div>
          <button className="page-btn" onClick={handleNext} disabled={currentPage === totalPages}>
            {t("history.pagination.next")}
          </button>
        </div>
      )}

      {data.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">👥</div>
          <h3>{t("history.allUsers.noActivity")}</h3>
          <p>{t("history.allUsers.noActivityDescription")}</p>
        </div>
      )}
    </div>
  );
};

export default AllUsersTab;
